import { SagaIterator } from 'redux-saga';
import { all, call, select, takeEvery, delay, put } from 'redux-saga/effects';
import { push } from 'react-router-redux';

import { globalConfig } from '@/globalConfig';

import { userModel } from '@entities/user';
import { actions } from './ducks';

import { selectors } from './selectors';
import { resetPassword } from '@features/Login/model/api';
import { loaderModel } from '@entities/loader';

function* persistLoginSession(jwtToken: string): SagaIterator {
  yield delay(0);
  yield call(
    [window.localStorage, window.localStorage.setItem],
    globalConfig.storage.tokenKey,
    jwtToken
  );
  yield call(
    [window.localStorage, window.localStorage.removeItem],
    globalConfig.storage.isAdmin
  );
  yield call(
    [window.localStorage, window.localStorage.removeItem],
    globalConfig.storage.userId
  );
  yield call(
    [window.localStorage, window.localStorage.removeItem],
    globalConfig.storage.requestId
  );
  yield put(userModel.actions.setRequestId(''));
  yield put(userModel.actions.setIsLoggedIn(true));
}

/**
 * Login by national ID + password → SMS OTP challenge
 */
function* loginByIdentitySaga(): SagaIterator {
  const identityNumber = yield select(selectors.id);
  const password = yield select(selectors.password);
  yield put(actions.setError(false));
  yield put(loaderModel.actions.setIsLoading(true));

  try {
    const data = yield call(
      userModel.api.loginByIdentityApi,
      identityNumber,
      password
    );
    yield put(userModel.actions.setConfirmSession(data.session));
    if (data.username) {
      yield put(userModel.actions.setUsername(data.username));
    }
  } catch (error: any) {
    console.log(error);
    yield put(
      actions.setNotExistUserValidation(
        error?.response?.data?.error?.message || 'תעודת זהות או סיסמא שגויה'
      )
    );
    yield put(actions.setError(true));
    console.warn('Faced some problems during login operation', error);
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

/**
 * Confirm login OTP after identity + password
 */
function* confirmOtpAfterLoginSaga(): SagaIterator {
  yield put(loaderModel.actions.setIsLoading(true));

  const session = yield select(userModel.selectors.confirmSession);
  const username = yield select(userModel.selectors.username);
  const code = yield select(selectors.otpCode);

  try {
    const data = yield call(
      userModel.api.confirmOtpCodeAfterLoginApi,
      session,
      username,
      code
    );
    if (data.jwtToken) {
      yield call(persistLoginSession, data.jwtToken);
      if (userModel.selectors.useIsFromSurvey()) {
        yield put(push(globalConfig.routes.personalArea()));
      } else {
        yield put(push(globalConfig.routes.main()));
      }
    }
    yield put(loaderModel.actions.setIsLoading(false));
  } catch (e) {
    if (e.response?.data?.error?.statusCode === 400) {
      yield put(
        actions.setErrorMessage(
          e?.response?.data?.error?.message || 'Unexpected login error'
        )
      );
    }
    yield put(actions.setError(true));
    yield put(loaderModel.actions.setIsLoading(false));
    console.warn('Faced some problems wile confirming login with OTP', e);
  }
}

/**
 * Resend OTP by re-running identity login (password + new challenge)
 */
function* resendOtpCodeSaga(): SagaIterator {
  yield call(loginByIdentitySaga);
}

function* restorePasswordSaga(): SagaIterator {
  const password = yield select(selectors.password);
  const password_verification = yield select(selectors.newPassword);
  const temporary_password = yield select(selectors.otpCode);
  const identity_number = yield select(selectors.id);

  yield put(actions.setError(false));
  yield put(loaderModel.actions.setIsLoading(true));

  try {
    const resetPasswordResponse = yield call(resetPassword, {
      identity_number,
      temporary_password,
      password,
      password_verification,
    });
    if (
      resetPasswordResponse?.status === 201 ||
      resetPasswordResponse?.data?.success ||
      resetPasswordResponse?.success
    ) {
      yield put(actions.setIsResetPasswordSuccess(true));
    }
  } catch (e: any) {
    yield put(actions.setError(true));
    yield put(
      actions.setErrorMessage(
        e?.response?.data?.error?.message ||
          e?.response?.data?.message ||
          'איפוס הסיסמה נכשל, נסה שנית'
      )
    );
  } finally {
    yield put(loaderModel.actions.setIsLoading(false));
  }
}

function* watcher(): SagaIterator<void> {
  yield all([
    takeEvery(actions.loginByIdentity, loginByIdentitySaga),
    takeEvery(actions.resendCode, resendOtpCodeSaga),
    takeEvery(actions.verifyCodeAfterLogin, confirmOtpAfterLoginSaga),
    takeEvery(actions.restorePassword, restorePasswordSaga),
  ]);
}

export const sagas = {
  watcher,
};
